import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { CountUp, FadeUp } from "./Reveal";
import { useI18n } from "@/context/Language";

export const DonationPanel = ({ fundraiser, onDonate }) => {
  const { t, lang } = useI18n();
  const P = t.panel;
  const raised = fundraiser?.raised ?? 0;
  const goal = fundraiser?.goal ?? 15000;
  const pct = Math.min(100, Math.round((raised / goal) * 100));
  const currency = fundraiser?.currency || "USD";
  const isLive = fundraiser?.source === "gofundme";
  const localeMap = { es: "es-ES", de: "de-DE", fr: "fr-FR", pt: "pt-PT", pl: "pl-PL", nl: "nl-NL", zh: "zh-CN" };
  const locale = localeMap[lang] || "en-US";
  const money = (n) =>
    n.toLocaleString(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });

  return (
    <section id="donate" className="relative z-10 -mt-2">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <FadeUp className="lg:col-span-7">
            <div className="bg-sand-2 border border-border-soft p-8 sm:p-10">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="font-serif text-5xl sm:text-6xl text-ink leading-none" data-testid="raised-amount">
                    {money(raised)}
                  </div>
                  <div className="mt-2 text-sm text-ink/60">{P.raisedLine(money(goal))}</div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-3xl text-olive leading-none">
                    <CountUp to={pct} />%
                  </div>
                  <div className="mt-2 text-sm text-ink/60">{P.donationsLabel(fundraiser?.donors_count ?? 0)}</div>
                </div>
              </div>

              {isLive && (
                <div className="mt-4 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-olive" data-testid="gofundme-live-badge">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-olive/60"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-olive"></span>
                  </span>
                  Live from GoFundMe
                </div>
              )}

              <div className="mt-7 h-3 w-full bg-secondary overflow-hidden" data-testid="progress-bar">
                <motion.div
                  className="h-full bg-olive"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${pct}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                />
              </div>

              <p className="mt-6 text-sm leading-relaxed text-ink/60">{P.info}</p>
              <p className="mt-3 text-xs leading-relaxed text-ink/50" data-testid="panel-manual-note">{P.manualUpdate}</p>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="border border-border-soft p-8 sm:p-10">
              <h3 className="font-serif text-2xl text-ink">{P.support}</h3>
              <p className="mt-2 text-sm text-ink/60">
                All donations are made securely through our GoFundMe campaign. Choose your amount on the next page.
              </p>

              <button
                data-testid="panel-donate-button"
                onClick={() => onDonate()}
                className="group mt-6 flex w-full items-center justify-between gap-3 rounded-2xl bg-terracotta px-6 py-5 text-sand transition-colors hover:bg-terracotta-hover active:scale-[0.99]"
              >
                <span className="flex flex-col items-start text-left">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-sand/70">
                    Secure donation via
                  </span>
                  <span className="mt-0.5 font-serif text-2xl leading-none">GoFundMe</span>
                </span>
                <span className="flex items-center gap-2 text-sm font-semibold">
                  Donate now
                  <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </button>

              <p className="mt-4 text-xs leading-relaxed text-ink/50" data-testid="panel-trust-note">
                {P.note}
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default DonationPanel;
